// Rich snippets optimization utilities for better search results

export interface RichSnippetConfig {
  type: 'faq' | 'howto' | 'recipe' | 'product' | 'review' | 'event' | 'breadcrumb' | 'article';
  data: any;
}

export interface FAQItem {
  question: string;
  answer: string;
}

export interface HowToStep {
  name: string;
  text: string;
  image?: string;
  url?: string;
}

export interface ProductInfo {
  name: string;
  description: string;
  image: string;
  brand: string;
  sku: string;
  price: number;
  currency: string;
  availability: 'InStock' | 'OutOfStock' | 'PreOrder';
  rating?: {
    value: number;
    count: number;
  };
}

export interface ReviewInfo {
  itemName: string;
  rating: number;
  reviewBody: string;
  author: string;
  datePublished: string;
}

/**
 * Generate FAQ rich snippet schema
 */
export const generateFAQSchema = (faqs: FAQItem[]): object => {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
};

/**
 * Generate HowTo rich snippet schema
 */
export const generateHowToSchema = (
  name: string,
  description: string,
  steps: HowToStep[],
  totalTime?: string,
  estimatedCost?: string
): object => {
  return {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name,
    description,
    ...(totalTime && { totalTime }),
    ...(estimatedCost && { estimatedCost: { '@type': 'MonetaryAmount', currency: 'USD', value: estimatedCost } }),
    supply: [],
    tool: [],
    step: steps.map((step, index) => ({
      '@type': 'HowToStep',
      position: index + 1,
      name: step.name,
      text: step.text,
      ...(step.image && { image: step.image }),
      ...(step.url && { url: step.url }),
    })),
  };
};

/**
 * Generate Product rich snippet schema
 */
export const generateProductSchema = (product: ProductInfo): object => {
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: product.description,
    image: product.image,
    brand: {
      '@type': 'Brand',
      name: product.brand,
    },
    sku: product.sku,
    offers: {
      '@type': 'Offer',
      price: product.price,
      priceCurrency: product.currency,
      availability: `https://schema.org/${product.availability}`,
      seller: {
        '@type': 'Organization',
        name: product.brand,
      },
    },
    ...(product.rating && {
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: product.rating.value,
        reviewCount: product.rating.count,
      },
    }),
  };
};

/**
 * Generate Review rich snippet schema
 */
export const generateReviewSchema = (review: ReviewInfo): object => {
  return {
    '@context': 'https://schema.org',
    '@type': 'Review',
    itemReviewed: {
      '@type': 'Thing',
      name: review.itemName,
    },
    reviewRating: {
      '@type': 'Rating',
      ratingValue: review.rating,
      bestRating: 5,
    },
    reviewBody: review.reviewBody,
    author: {
      '@type': 'Person',
      name: review.author,
    },
    datePublished: review.datePublished,
  };
};

/**
 * Generate Article rich snippet schema with enhanced features
 */
export const generateArticleSchema = (
  title: string,
  description: string,
  author: string,
  publishDate: string,
  modifiedDate: string,
  image: string,
  url: string,
  wordCount?: number
): object => {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description,
    image: {
      '@type': 'ImageObject',
      url: image,
      width: 1200,
      height: 630,
    },
    author: {
      '@type': 'Person',
      name: author,
    },
    publisher: {
      '@type': 'Organization',
      name: 'FortifyTek',
      logo: {
        '@type': 'ImageObject',
        url: 'https://www.fortifytek.com/logo.png',
        width: 200,
        height: 60,
      },
    },
    datePublished: publishDate,
    dateModified: modifiedDate,
    url,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': url,
    },
    ...(wordCount && { wordCount }),
  };
};

/**
 * Generate Event rich snippet schema
 */
export const generateEventSchema = (
  name: string,
  description: string,
  startDate: string,
  endDate: string,
  location: {
    name: string;
    address: string;
  },
  organizer: string,
  url?: string
): object => {
  return {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name,
    description,
    startDate,
    endDate,
    location: {
      '@type': 'Place',
      name: location.name,
      address: {
        '@type': 'PostalAddress',
        streetAddress: location.address,
      },
    },
    organizer: {
      '@type': 'Organization',
      name: organizer,
    },
    ...(url && { url }),
  };
};

/**
 * Auto-detect and generate appropriate rich snippets from content
 */
export const autoGenerateRichSnippets = (content: string, metadata: any): object[] => {
  const snippets: object[] = [];

  // Detect FAQ content
  const faqMatches = content.match(/(?:^|\n)(?:Q:|Question:|FAQ:)\s*(.+?)(?:\n|$)(?:A:|Answer:)\s*(.+?)(?=\n(?:Q:|Question:|FAQ:)|\n\n|$)/gim);
  if (faqMatches && faqMatches.length >= 2) {
    const faqs: FAQItem[] = [];
    const faqRegex = /(?:Q:|Question:|FAQ:)\s*(.+?)(?:\n|$)(?:A:|Answer:)\s*(.+?)(?=\n(?:Q:|Question:|FAQ:)|\n\n|$)/gim;
    let match;
    while ((match = faqRegex.exec(content)) !== null) {
      faqs.push({
        question: match[1].trim(),
        answer: match[2].trim(),
      });
    }
    if (faqs.length > 0) {
      snippets.push(generateFAQSchema(faqs));
    }
  }

  // Detect How-To content
  const howToMatches = content.match(/(?:step\s+\d+|^\d+\.)\s*(.+?)(?=(?:step\s+\d+|^\d+\.)|$)/gim);
  if (howToMatches && howToMatches.length >= 3) {
    const steps: HowToStep[] = howToMatches.map((step, index) => ({
      name: `Step ${index + 1}`,
      text: step.replace(/^(?:step\s+\d+|^\d+\.)\s*/i, '').trim(),
    }));
    
    snippets.push(generateHowToSchema(
      metadata.title || 'How-To Guide',
      metadata.description || 'Step-by-step instructions',
      steps
    ));
  }

  // Generate Article schema for blog posts
  if (metadata.type === 'article' || metadata.type === 'blog') {
    snippets.push(generateArticleSchema(
      metadata.title,
      metadata.description,
      metadata.author || 'FortifyTek',
      metadata.publishDate || new Date().toISOString(),
      metadata.modifiedDate || new Date().toISOString(),
      metadata.image || '/images/default-og.jpg',
      metadata.url || '',
      content.split(/\s+/).length
    ));
  }

  return snippets;
};

/**
 * Optimize content for featured snippets
 */
export const optimizeForFeaturedSnippets = (content: string, targetKeyword: string): {
  optimizedContent: string;
  suggestions: string[];
} => {
  const suggestions: string[] = [];
  let optimizedContent = content;

  // Add definition format for "What is" queries
  if (targetKeyword.toLowerCase().startsWith('what is')) {
    const term = targetKeyword.replace(/^what is\s+/i, '');
    if (!content.includes(`${term} is`)) {
      suggestions.push(`Add a clear definition: "${term} is..." at the beginning`);
    }
  }

  // Add numbered lists for "How to" queries
  if (targetKeyword.toLowerCase().startsWith('how to')) {
    const hasNumberedList = /^\d+\.\s/.test(content);
    if (!hasNumberedList) {
      suggestions.push('Structure content as numbered steps for "How to" queries');
    }
  }

  // Add comparison tables for "vs" or "best" queries
  if (targetKeyword.toLowerCase().includes(' vs ') || targetKeyword.toLowerCase().includes('best ')) {
    const hasTable = content.includes('|') || content.includes('<table>');
    if (!hasTable) {
      suggestions.push('Add comparison table for better featured snippet chances');
    }
  }

  // Optimize paragraph length (40-50 words for featured snippets)
  const paragraphs = content.split('\n\n');
  paragraphs.forEach((paragraph, index) => {
    const wordCount = paragraph.split(/\s+/).length;
    if (wordCount > 60) {
      suggestions.push(`Paragraph ${index + 1} is too long (${wordCount} words). Aim for 40-50 words for featured snippets.`);
    }
  });

  return { optimizedContent, suggestions };
};

/**
 * Generate breadcrumb rich snippet schema
 */
export const generateBreadcrumbSchema = (breadcrumbs: Array<{ name: string; url: string }>): object => {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: crumb.url,
    })),
  };
};

/**
 * Generate video rich snippet schema
 */
export const generateVideoSchema = (
  name: string,
  description: string,
  thumbnailUrl: string,
  uploadDate: string,
  duration: string,
  contentUrl?: string
): object => {
  return {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name,
    description,
    thumbnailUrl,
    uploadDate,
    duration,
    ...(contentUrl && { contentUrl }),
  };
};

/**
 * Validate rich snippet markup
 */
export const validateRichSnippets = (schema: object): {
  isValid: boolean;
  errors: string[];
  warnings: string[];
} => {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Basic validation
  if (!schema['@context']) {
    errors.push('Missing @context property');
  }

  if (!schema['@type']) {
    errors.push('Missing @type property');
  }

  // Type-specific validation
  const type = schema['@type'];
  
  if (type === 'FAQPage') {
    if (!schema['mainEntity'] || !Array.isArray(schema['mainEntity'])) {
      errors.push('FAQPage must have mainEntity array');
    }
  }

  if (type === 'Product') {
    if (!schema['name']) errors.push('Product must have name');
    if (!schema['offers']) errors.push('Product must have offers');
  }

  if (type === 'Article') {
    if (!schema['headline']) errors.push('Article must have headline');
    if (!schema['author']) errors.push('Article must have author');
    if (!schema['datePublished']) errors.push('Article must have datePublished');
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
};